import React, { Fragment } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { withCookies } from 'react-cookie';
import { withRouter } from 'react-router-dom';
    
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Grid, Tooltip, Typography, Paper, TextField, Collapse, IconButton, Button } from '@material-ui/core'
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import Chart from "react-apexcharts";

import { Account } from '../models/Account'
import { AgsUser } from '../models/AgsUser'
import { ThemeColors } from '../Theme'
import { AccountStatWidget } from '../components/AccountStatWidget'
import { AccountViewPopover } from '../components/AccountViewPopover';

import { RestComponent, SummaryWidget, RichTextEditor, plaintextToRichTextJson } from 'react-frontend-utils' 


const colorNone = '#AAAAAA';
const colorGreen = '#45bf4b';
const colorYellow = '#e8d900';
const colorBlue = '#1f99ff';
const colorRed = '#ff4d4d';
const colorOrange = '#ee8012ff';
const colorPurple = '#a348db';

const seriesColors = ['#37b37f', colorBlue, '#a348db', '#ff7099', colorYellow];

const AccoutTooltip = withStyles((theme) => ({
    tooltip: {
        backgroundColor: 'white',
        color: 'black',
        maxWidth: 900,
        fontSize: theme.typography.pxToRem(16),
        border: '2px solid gray',
        padding: 5
    }
}))(Tooltip);

class StatFilter {

    static ApplicationStatusEnum = [{value: 'NONE', label: 'Disabled', color: colorRed}, {value: 'SELF_SERVICE', label: 'Self Service', color:  colorBlue}, {value: 'FULL_SERVICE', label: 'Full Service', color: colorGreen}];
    static PassPrintingEnum = [{value: 'NONE', label: 'Disabled', color: colorRed}, {value: 'DIRECT_MAIL', label: 'Direct Mail', color: colorBlue}, {value: 'BULK_MAIL', label: 'Bulk Mail', color: colorGreen}, {value: 'SELF_PRINT', label: 'Self Print', color: colorOrange}];

    options;    // object with keys: value, label, color (optional)
    label;
    selection;

    constructor(options, queryKey, label) {
        this.options = options;
        this.queryKey = queryKey;
        this.label = label;
    }

    static createStatFilters(agsReps) {
        let filters = [];

        const enableDisableOptions = [{value: null, label: 'Any'}, {value: 'true', label: 'Enabled', color: colorGreen}, {value: 'false', label: 'Disabled', color: colorRed}]; 

        const stateOptions = Object.values(Account.State).map(s => {return {value: s.enumName, label: s.label, color: s.color}});
        let filterOptions = [{value: null, label: 'Any'}, ...stateOptions];
        const stateFilter = new StatFilter(filterOptions, "state", "Account State")
        // Default to ACTIVE
        stateFilter.selection = filterOptions.find(o => o.value === Account.State.ACTIVE.enumName);
        filters.push(stateFilter);

        const phaseOptions = Object.values(Account.Phase).map(p => {return {value: p.enumName, label: p.label, color: p.color}});
        filterOptions = [{value: null, label: 'Any'}, ...phaseOptions];
        filters.push(new StatFilter(filterOptions, "phase", "Account Phase"));
   
        filters.push(new StatFilter(
            [{value: null, label: 'Any'}, {value: 'true', label: 'Attention Needed', color: colorYellow}, {value: 'false', label: 'OK', color: colorGreen}],
            "needsAttention",
            "Attention"
        ));

        const templateOptions = [{value: null, label: 'Any'}, {value: 'true', label: 'Client Controlled', color: colorGreen}, {value: 'false', label: 'AGS Controlled', color: colorBlue}]; 

        const repOptions = agsReps.map(r => {return {value: r.id, label: r.fullName(), color: colorBlue}});
        filterOptions = [{value: null, label: 'Any'}, ...repOptions];
        filters.push(new StatFilter(filterOptions, "repId", "Representative"));

        filters.push(new StatFilter(enableDisableOptions, "hasCheckIn", "Check-In"));

        filters.push(new StatFilter([{value: null, label: 'Any'}, ...StatFilter.ApplicationStatusEnum], "appStatus", "Applications"));

        filters.push(new StatFilter(enableDisableOptions, "hasMergedDocuments", "Merged Documents"));

        filters.push(new StatFilter(enableDisableOptions, "ePassesEnabled","ePasses"));

        filters.push(new StatFilter([{value: null, label: 'Any'}, ...StatFilter.PassPrintingEnum], "passPrinting", "Pass Printing"));

        filters.push(new StatFilter(enableDisableOptions, "hasBookingEnabled", "Booking"));

        filters.push(new StatFilter(templateOptions, "hasClientTemplates", "Application Templates"));

        filters.push(new StatFilter(
            [{value: null, label: 'Any'}, {value: 'true', label: 'Yes', color: colorGreen}, {value: 'false', label: 'No', color: colorBlue}],
            "hasMultiAccount",
            "Multiple Accounts"
        ));

        let yearOptions = [{value: null, label: 'Any'}];
        yearOptions.push({value: 0, label: 'N/A', color: 'black'});
        for (let year = StatisticsPage.START_YEAR; year <= StatisticsPage.currentYear + 1; year++)
            yearOptions.push({value: year, label: year.toString(), color: colorPurple});
        filters.push(new StatFilter(yearOptions, "yearAcquired", "Year Acquired"));

        for (let filter of filters) {
            if (filter !== stateFilter)
                filter.selection = filter.options[0];   // default to 'Any'
        }

        return filters;
    }

    renderFilter(filterChangeCallback) {
        return <Autocomplete
                    style={{maxWidth: 400, minWidth: 300}}
                    disableClearable={true}
                    size='small'
                    value={this.selection}
                    getOptionLabel={(option) => option.label}
                    onChange={(event, newValue) => { this.selection = newValue; filterChangeCallback(); }}
                    options={this.options}
                    blurOnSelect
                    renderOption={(option) => {
                        return (
                        <span style={{ color: option.color ? option.color : colorNone }}>
                            {option.label}
                        </span>
                        );
                    }}
                    renderInput={(params) => <TextField {...params} label={this.label} variant="outlined" InputLabelProps={{ shrink: true }} 
                                             InputProps={{ ...params.InputProps, style: {color: this.selection.color ? this.selection.color : colorNone, fontWeight: this.selection.value ? 'bold' : 'normal'} }}/>}
                />
    }

}



export class StatisticsPage extends RestComponent {

    styles = {
        paperLabel: {
            marginLeft: 15,
            marginRight: 15,
            marginBottom: 5,
            color: 'gray',
            fontSize: '12pt',
            flexGrow: 1
        },
        paper: {
            padding: 0,
            marginBottom: 20
        },
        delayLabel: {
            marginLeft: 15,
            marginBottom: 20,
            textAlign: 'right',
            fontStyle: 'italic',
            color: 'gray',
            fontSize: '12pt',
        },
        table: {
            borderCollapse: 'collapse',
            width: '100%',
            marginBottom: 10,
            marginTop: 10
        },
        tableHeader: {
            borderBottom: '2px solid ' + ThemeColors.appBarBackground,
            textAlign: 'left',
            paddingBottom: 5,
            paddingRight: 10,
            marginBottom: 8,
            fontSize: 14,
            color: ThemeColors.darkGray,
            fontWeight: 'normal',
            textTransform: 'uppercase'
        },
        tableDataWithBorder: {
            textAlign: 'left',
            fontSize: 14,
            paddingRight: 10,
            borderBottom: '1px solid lightGray'
        },
    };

    _acquireYearChartOpts = {
        chart : {
            stacked: false,
            zoom: {
                enabled: false
            },
            toolbar: {
                show: false
            },
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 600,
                animateGradually: {
                    enabled: false
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 350
                }
            }
        },
        colors: [colorPurple],  
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "70%"
            }
        },
        xaxis: {
            categories: [],
        },
        dataLabels: {
            enabled: true,
        }
    };

    
    static START_YEAR = 2015;
    static currentYear = (new Date()).getFullYear();

    static sortOptions = [
        {value: 'databaseName', label: 'Database Name', type: 'string'},
        {value: 'extendedName', label: 'Community Name', type: 'string'},
        {value: 'accountNumber', label: 'Account Number', type: 'number'},
        {value: 'repName', label: 'Representative', type: 'string'},
        {value: 'hocName', label: 'HOC Name', type: 'string'},
        {value: 'state', label: 'State', type: Account.State},
        {value: 'phase', label: 'Phase', type: Account.Phase},
        {value: 'yearAcquired', label: 'Year Acquired', type: 'number'},
        {value: 'communityType', label: 'Community Type', type: 'communityType'},
        {value: 'needsAttention', label: 'Needs Attention', type: 'boolean'},
        {value: 'hasMultiAccounts', label: 'Multiple Accounts', type: 'hasMulti'},
    ];

    _tableWrapperRef = React.createRef();
    static TABLE_MIN_HEIGHT = 200;

    constructor(props) {
        super(props);

        this.state.filters = null;
        this.state.accountStats = null;
        this.state.miniAccountList = [];
        this.state.showGraphs = true;
        this.state.filterText = "";
        this.state.tableMinHeight = StatisticsPage.TABLE_MIN_HEIGHT;

        this.state.accountPopoverOpen = false;
        this.state.accountInPopover = null;

        this.state.sortBy = StatisticsPage.sortOptions[0]; // default sort by Database Name

        this._acquireYearChartOpts.xaxis.categories.push("N/A");
        for (let i = StatisticsPage.START_YEAR; i <= StatisticsPage.currentYear; i++)
            this._acquireYearChartOpts.xaxis.categories.push(i.toString());
    }

    componentDidMount() {
        super.componentDidMount();
        this._fetchStatistics();
    }


    _fetchStatistics = () => {

        let queryString = "";
        if (this.state.filters) {
            for (const filter of this.state.filters) {
                if (filter.selection.value != null) {
                    if (queryString.length > 1)
                        queryString += "&";
                    else
                        queryString += "?";
                    queryString += filter.queryKey + "=" + filter.selection.value;
                }
            }
        }

                
        this.incrementBusy();
        this.secureJSONFetch("/act/statistics" + queryString, {}, this._fetchStatisticsCallback, this._fetchErrorCallback);     
    }
    
    _fetchStatisticsCallback = (response) => {
        if (response) {           
            if (this.state.filters == null) {   // first time only
                const availableReps = response.availableReps ? response.availableReps.map(r => new AgsUser(r)) : [];
                const statFilters = StatFilter.createStatFilters(availableReps);
                this.setState({filters: statFilters});
                setTimeout(() => this._fetchStatistics()); // fetch again with filters initialized
                return;
            }
            this.setState({accountStats: response});
            this._sortByCallback(this.state.sortBy, response.miniAccountList);
        }            
        this.decrementBusy();
    }
    
    _fetchErrorCallback = (error) => {
        this.showConfirmAlert("Error", error, 'red');
        this.decrementBusy();
    }


    _sortByCallback = (newValue, currentList) => {
        let sortedList = [...currentList];
        sortedList.sort((a, b) => {
            let valA = a[newValue.value];
            let valB = b[newValue.value];

            if (newValue.type === Account.State) {
                valA = Account.stateFromString(valA).bit;
                valB = Account.stateFromString(valB).bit;
            }   
            else if (newValue.type === Account.Phase) {
                valA = Account.phaseFromString(valA).bit;
                valB = Account.phaseFromString(valB).bit;
            }

            if (newValue.type === 'string') {
                valA = valA ? valA.toLowerCase() : "";
                valB = valB ? valB.toLowerCase() : "";
            }
            else if (newValue.type === 'communityType') {
                valA = valA ? Account.communityTypeFromString(valA).label.toLowerCase() : "\uffff";   // unknown last
                valB = valB ? Account.communityTypeFromString(valB).label.toLowerCase() : "\uffff";
            }
            else if (newValue.type === 'boolean') {
                valA = valA ? 0 : 1;  // true values first
                valB = valB ? 0 : 1;  // true values first
            }
            else if (newValue.type === 'hasMulti') {
                valA = valA ? 0 : 1;  // true values first
                valB = valB ? 0 : 1;  // true values first

                if (valA === valB) // equal, sort by HOC name
                    return a.hocName.localeCompare(b.hocName);
            }
            
            if (valA < valB)
                return -1;
            else if (valA > valB)
                return 1;
            else { // equal, sort by database name
                if (a.databaseName < b.databaseName)
                    return -1;
                else if (a.databaseName > b.databaseName)
                    return 1;
                else
                    return 0;
            }
        });

        this.setState({sortBy: newValue, miniAccountList: sortedList, tableMinHeight: StatisticsPage.TABLE_MIN_HEIGHT});
    }


    _showAccount = (accountNo) => {
        this.secureJSONFetch("/act/account/" + accountNo, {}, this._fetchAccountCallback, this._fetchErrorCallback); 
    }

    _fetchAccountCallback = (response) => {
        if (response) {            
            const act = new Account(response);
            this.setState({accountInPopover: act, accountPopoverOpen: true});
        }            
    }

    _clearFilters = () => {
        if (this.state.filters) {
            for (let filter of this.state.filters)
                filter.selection = filter.options[0];   // reset to 'Any'
            this.setState({statFilters: this.state.statFilters}, this._fetchStatistics);
        }
    }
    
    _copyAllEmailAddresses = () => {
        let emailList = "";
        let count = 0;
        for (const miniAccount of this.state.miniAccountList) {
            if (miniAccount.hocEmail) {
                if (emailList.length > 0)
                    emailList += "; ";
                    
                emailList += miniAccount.hocEmail;
                count++;
            }
        }
        if (count > 0) {
            navigator.clipboard.writeText(emailList);
            this.showConfirmAlert("Copied", "Copied " + count + " email addresses to clipboard.", 'green');
        }
        else 
            this.showConfirmAlert("No Emails", "No HOC emails to copy.", 'red');
    }


    static renderNotes = (label, notes) => {
            return (
                <Fragment>
                    <Typography variant="body2" style={{marginBottom: 5, fontWeight: 'bold'}}>{label}</Typography>
                    <div style={{border: '1px solid #CCCCCC', borderRadius: '4px', padding: 10, paddingBottom: 15}}>
                        <RichTextEditor
                            draftContent={notes}     
                            editing={false} 
                        />
                    </div>
                </Fragment>
            );
        }

    static accountTooltipContent(miniAccount) {

        const features = []
        if (miniAccount.hasCheckIn)
            features.push("Check-In");
        if (miniAccount.applicationStatus !== 'NONE')
            features.push("Applications: " + miniAccount.applicationStatus.replace('_', ' ').toLowerCase());
        if (miniAccount.mergedDocuments)
            features.push("Merged Documents");
        if (miniAccount.ePassesEnabled)
            features.push("ePasses");
        if (miniAccount.passPrinting !== 'NONE')
            features.push("Pass Printing: " + miniAccount.passPrinting.replace('_', ' ').toLowerCase());
        if (miniAccount.bookingEnabled)
            features.push("Booking");
        if (miniAccount.clientControlledTemplates)
            features.push("Client Controlled Templates");


        // Upgrade plaintext account notes to rich text, if they didn't start with a { they are not json
        let accountNotesJson = miniAccount.accountNotes;
        if (accountNotesJson && !accountNotesJson.startsWith("{")) {
            accountNotesJson = plaintextToRichTextJson(accountNotesJson);
        }

        let nextActionNotesJson = miniAccount.nextActionsNotes;
        if (nextActionNotesJson && !nextActionNotesJson.startsWith("{")) {
            nextActionNotesJson = plaintextToRichTextJson(nextActionNotesJson);
        }


        return <div>
                    <Typography variant="h6" style={{marginBottom: 20, borderBottom: '2px solid ' + ThemeColors.appBarBackground}}>{miniAccount.extendedName}</Typography>
                    <div style={{display: 'flex', alignItem: 'top', gap: 30}}>
                        <div style={{minWidth: '30%'}}>
                            <Typography variant="body2"><b>Year Acquired:</b> {miniAccount.yearAcquired}</Typography>
                            <Typography variant="body2"><b>Community Type:</b> {Account.communityTypeFromString(miniAccount.communityType).label}</Typography>
                            <Typography variant="body2"><b>Features:</b></Typography>
                            <ul style={{marginTop: 0, marginBottom: 20}}>
                                {features.map((f, index) => <li key={index} style={{fontSize: 14}}>{f}</li>)}
                            </ul>
                            {miniAccount.needsAttention ? <Typography variant="body1"><b>⚠️ Needs Attention</b></Typography> : null}
                        </div>
                        <div>
                            {StatisticsPage.renderNotes("Account Notes:", accountNotesJson)}
                            <div style={{height: 20}}></div>
                            {StatisticsPage.renderNotes("Next Actions Notes:", nextActionNotesJson)}
                        </div>
                    </div>
                </div>
    }

    //Render a row of the table with the specified MiniAccount. In the MiniAccount is null, render the header row
    _renderRow = (miniAccount, key, filterText) => {
        
        //Render the header
        if (!miniAccount) {
            return (
                <tr key={key} style={this.styles.tableStyle}>
                    <th style={{...this.styles.tableHeader, textAlign: 'center', width: 180}}>Account</th>
                    <th style={this.styles.tableHeader}>Database</th>
                    <th style={{...this.styles.tableHeader, width: 50}}></th>
                    <th style={this.styles.tableHeader}>Community Name</th>
                    <th style={this.styles.tableHeader}>Representative</th>
                    <th style={this.styles.tableHeader}>State</th>
                    <th style={this.styles.tableHeader}>Phase</th>
                    <th style={this.styles.tableHeader}>Multi</th>
                    <th style={this.styles.tableHeader}>HOC</th>
                </tr>
            );
        }
                            
        const state = Account.stateFromString(miniAccount.state);
        const phase = Account.phaseFromString(miniAccount.phase);
        
        let hocValue = ""
        if (miniAccount.hocName)
            hocValue = miniAccount.hocName + " (" + miniAccount.hocEmail + ")";

        // Hide this row when the filter text (if valid) doesn't occur in any of these account fields.
        if (filterText && ![hocValue, miniAccount.databaseName, miniAccount.extendedName, miniAccount.accountNumber]
            .some(value => String(value || "").toLowerCase().includes(filterText.toLowerCase())))
            return null;

        
        return (
            <Fragment key={key}>
                <tr style={this.styles.tableStyle}>

                    <td style={{...this.styles.tableDataWithBorder, textAlign: 'center'}}>
                        <Button onClick={() => this._showAccount(miniAccount.accountNumber)}>{miniAccount.accountNumber}</Button> 
                    </td>

                    <AccoutTooltip title={StatisticsPage.accountTooltipContent(miniAccount)}>
                        <td style={this.styles.tableDataWithBorder}>{miniAccount.databaseName}</td>
                    </AccoutTooltip>

                    <th style={{...this.styles.tableDataWithBorder, width: 20, fontSize: 22}}>{miniAccount.needsAttention ? '⚠️' : ''}</th>
                    <td style={this.styles.tableDataWithBorder}>{miniAccount.extendedName}</td>
                    <td style={this.styles.tableDataWithBorder}>{miniAccount.repName}</td>
                    <td style={{...this.styles.tableDataWithBorder, color: state.color}}>{state.label}</td>
                    <td style={{...this.styles.tableDataWithBorder, color: phase.color}}>{phase.label}</td>
                    <td style={this.styles.tableDataWithBorder}>{miniAccount.hasMultiAccounts ? 'Multi' : ''}</td>
                    <td style={this.styles.tableDataWithBorder}>{hocValue}</td>
                </tr>
            </Fragment>
        );
    }

    _filterTextChanged = (event) => {
        const filterText = event.target.value;
        let tableHeight = StatisticsPage.TABLE_MIN_HEIGHT;      // default min table height

        // Preserve the table's current height while filtering so page scroll position stays stable.
        if (this._tableWrapperRef.current) {
            const currHeight = this._tableWrapperRef.current.getBoundingClientRect().height;
            if (currHeight > tableHeight)
                tableHeight = currHeight;
        }

        this.setState({filterText: filterText, tableMinHeight: tableHeight});
    }


    render() {
        const stat = this.state.accountStats;

        if (!stat)
            return this.getConfirmAlertComponent();

        const freePlanPie = [{label: "Paid Plan", value: stat.totalAccounts - stat.checkInDisabledCount},
                           {label: "Free Plan", value: stat.checkInDisabledCount}];
        const planColors = [colorGreen, colorYellow];

        const marketplacePie = [{label: "Enabled", value: stat.marketplaceEnabledCount},
                                {label: "Disabled", value: stat.totalAccounts - stat.marketplaceEnabledCount}];
        const marketColors = [colorGreen, colorNone];

        const bookingPie = [{label: "Enabled", value: stat.bookingEnabledCount},
                            {label: "Disabled", value: stat.totalAccounts - stat.bookingEnabledCount}];
        const bookingColors = [colorGreen, colorNone];

        const templatesPie = [{label: "Client Controlled", value: stat.clientControlledTemplatesCount},
                              {label: "AGS Controlled", value: stat.totalAccounts - stat.clientControlledTemplatesCount}];
        const templatesColors = [colorGreen, colorBlue];

        const selfPrintPie = [{label: "Enabled", value: stat.selfPrintEnabledCount},
                            {label: "Disabled", value: stat.totalAccounts - stat.selfPrintEnabledCount}];
        const selfPrintColors = [colorOrange, colorNone];

        const noApplications = stat.totalAccounts -  stat.applicationFullServiceCount - stat.applicationSelfServiceCount;
        const applicationsPie = [{label: "Self Service", value: stat.applicationSelfServiceCount},
                                 {label: "Full Service", value: stat.applicationFullServiceCount},
                                 {label: "None", value: noApplications}];
        const applicationColors = [colorBlue, colorGreen, colorNone];


        const ePassesPie = [{label: "Enabled", value: stat.ePassesEnabledCount},
                          {label: "Disabled", value: stat.totalAccounts - stat.ePassesEnabledCount}];
        const ePassesColors = [colorGreen, colorNone];

        const noPassPrintingCount = stat.totalAccounts -  stat.directMailCount - stat.bulkMailCount;
        const classicPassMailingPie = [{label: "Direct Mailed", value: stat.directMailCount},
                                       {label: "Bulk Mailed", value: stat.bulkMailCount},
                                       {label: "None", value: noPassPrintingCount}];
        const passesColors = [colorBlue, colorGreen, colorNone];


        const mergedDocPie = [{label: "Enabled", value: stat.mergedDocumentCount},
                              {label: "Disabled", value: stat.totalAccounts - stat.mergedDocumentCount}];
        const mergedColors = [colorGreen, colorNone];

        const flaggedPie = [{label: "Flagged", value: stat.flaggedCount},
                            {label: "OK", value: stat.totalAccounts - stat.flaggedCount}];
        const flaggedColors = [colorRed, colorNone];

        const summertimePie = [{label: "Summer Seasonal", value: stat.summerOnlyCount},
                               {label: "Year Round", value: stat.totalAccounts - stat.summerOnlyCount}];
        const summertimeColors = [colorGreen, colorBlue];
                    
        let statePie = [];
        let stateColors = [];
        // Fill the arrays based on the State enum
        for (const stateEnum of Object.values(Account.State)) {
            statePie[stateEnum.bit] = {label: stateEnum.label, value: 0};
            stateColors[stateEnum.bit] = stateEnum.color;
        }

        for (const stateStat of stat.stateCount) {
            const stateEnum = Account.stateFromString(stateStat.state);
            if (!stateEnum) {
                console.error("Unknown state: " + stateStat.state);
                continue;
            }
            statePie[stateEnum.bit].value = stateStat.count;
        }

        // Fill the arrays based on the Phase enum
        let phasePie = [];
        let phaseColors = [];
        for (const phaseEnum of Object.values(Account.Phase)) {
            phasePie[phaseEnum.bit] = {label: phaseEnum.label, value: 0};
            phaseColors[phaseEnum.bit] = phaseEnum.color;
        }

        for (const phaseStat of stat.phaseCount) {
            const phaseEnum = Account.phaseFromString(phaseStat.phase);
            if (!phaseEnum) {
                console.error("Unknown phase: " + phaseStat.phase);
                continue;
            }
            phasePie[phaseEnum.bit].value = phaseStat.count;
        }
                
        // Sales tax states, small slices are grouped into "Other" to keep the pie readable
        const OTHER_STATE_THRESHOLD = 0.05;
        let salesTaxStatePie = [];
        let otherStateCount = 0;
        let otherStateDetail = [];
        for (const taxState of stat.taxStateCount) {
            if (stat.totalAccounts > 0 && taxState.state && taxState.count / stat.totalAccounts < OTHER_STATE_THRESHOLD) {
                otherStateCount += taxState.count;
                otherStateDetail.push({state: taxState.state, count: taxState.count});
            }
            else
                salesTaxStatePie.push({label: taxState.state || "Outside US", value: taxState.count});
        }
        if (otherStateCount > 0) {
            otherStateDetail.sort((a, b) => a.state.localeCompare(b.state));
            salesTaxStatePie.push({label: "Other", value: otherStateCount});
        }

        const communityTypePie = stat.communityTypeCount.map(c => ({label: Account.communityTypeFromString(c.communityType).label, value: c.count}));

        let accountRepPie = [];
        for (const rep of stat.accountRepCount)
            accountRepPie.push({label: rep.name ? rep.name : "Unassigned", value: rep.count});
        
        let payoutModePie = [];
        for (const mode of stat.payoutModeCount)
            payoutModePie.push({label: mode.payoutMode, value: mode.count});
            
        const autoPayPie = [{label: "Enrolled", value: stat.autoPaySignedUpCount},
                            {label: "Not Enrolled", value: stat.totalAccounts - stat.autoPaySignedUpCount}];
        const autoPayColors = [colorGreen, colorNone];


        const customFeePie = [{label: "Has Custom Fees", value: stat.hasCustomFees},
                              {label: "Uses Default Fees", value: stat.totalAccounts - stat.hasCustomFees}];
        const customFeeColors = [colorYellow, colorBlue];

   

        let yearAcquiredSeries = [];
        for (let i = 0; i < this._acquireYearChartOpts.xaxis.categories.length; i++)
            yearAcquiredSeries[i] = 0;

        for (const acq of stat.yearAcquiredCount) {
            if (acq.year === 0)
                yearAcquiredSeries[0] += acq.count;        // first element is "N/A"
            else {
                const index = acq.year - StatisticsPage.START_YEAR + 1;
                if (index < 1 || index > StatisticsPage.currentYear) {     // unknown year
                    console.error("Year out of range: " + acq.year);
                    yearAcquiredSeries[0] += acq.count;
                }
                else
                    yearAcquiredSeries[index] += acq.count;
            }
        }

        const yearAcquiredChartData = [
            {
                name: "Accounts Acquired",
                data: yearAcquiredSeries
            }
        ];

        let filters = null;
        if (this.state.filters) {
            filters = this.state.filters.map((f, index) => (
                <div key={index} style={{marginRight: 20, marginBottom: 10}}>
                    {f.renderFilter(() => this.setState({statFilters: this.state.statFilters}, this._fetchStatistics))}
                </div>
            ));
        }

        const showGraphsIcon = this.state.showGraphs ? <ArrowDropDownIcon/> : <ArrowRightIcon/>;

        return (                        
             <Fragment>
                {this.getConfirmAlertComponent()}  

                <AccountViewPopover isOpen={this.state.accountPopoverOpen} account={this.state.accountInPopover} onClose={() => this.setState({accountPopoverOpen: false})}/>

                <Typography variant="body2" style={this.styles.delayLabel}>Statistics delayed by up to 30 minutes</Typography>  

                <Paper style={{...this.styles.paper, padding: 20, paddingBottom: 10}}>
                    <Grid container direction="row" spacing={3} style={{justifyContent: 'left', marginLeft: 0, marginTop: 0}}>
                        {filters}
                    </Grid>
                    <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 10, marginRight: 0}}>
                        <Button variant='outlined' size='small' onClick={this._clearFilters} >Clear Filters</Button>  
                    </div>
                </Paper>

                <Grid container direction="row" spacing={2} style={{padding: 10, marginTop: 10, justifyContent: 'center'}}>
                    <Grid item xs={3}>
                        <SummaryWidget label={"Matching Accounts"}
                                    value={stat.totalAccounts}
                                    borderColor='black'/>
                    </Grid>
                    {/**  disabled
                    <Grid item xs={3}>
                        <SummaryWidget label={"Databases Suspended"}
                                    value={stat.suspendedCount}
                                    tooltip="Number of real Accounts suspended in PoolPass"
                                    borderColor='red'/>
                    </Grid>
                    */}
                </Grid>

                <div style={{display: 'flex', alignItems: 'center', marginBottom: 10}}>
                    <IconButton edge="start" onClick={() => this.setState({showGraphs: !this.state.showGraphs})} >
                        {showGraphsIcon}
                    </IconButton>
                    <Typography variant="h6" style={{flexGrow: 1, marginLeft: 10}}>Graphs</Typography>
                </div>
                        
                <Collapse in={this.state.showGraphs}>
                    <Paper style={this.styles.paper}>
                        
                        <Typography variant="body2" style={this.styles.paperLabel}>Features</Typography>  

                        <Grid container direction="row" spacing={2} style={{padding: 10, marginTop: 10, justifyContent: 'center'}}>

                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Account State"} items={statePie} pieColors={stateColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Account Phase"} items={phasePie} pieColors={phaseColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Flagged Accounts"} items={flaggedPie} pieColors={flaggedColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Seasonal"} items={summertimePie} pieColors={summertimeColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Membership Portal Plan"} items={freePlanPie} pieColors={planColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Marketplaces Enabled"} items={marketplacePie} pieColors={marketColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Booking Enabled"} items={bookingPie} pieColors={bookingColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Application Templates"} items={templatesPie} pieColors={templatesColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Application Processing"} items={applicationsPie} pieColors={applicationColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"ePasses Enabled"} items={ePassesPie} pieColors={ePassesColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Pass Printing"} items={classicPassMailingPie} pieColors={passesColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Self Printing"} items={selfPrintPie} pieColors={selfPrintColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Merged Documents"} items={mergedDocPie} pieColors={mergedColors}/>
                            </Grid>

                        </Grid>
                    </Paper>


                    <Paper style={this.styles.paper}>
                        
                        <Typography variant="body2" style={this.styles.paperLabel}>Settings</Typography>  

                        <Grid container direction="row" spacing={2} style={{padding: 10, marginTop: 10, justifyContent: 'center'}}>

                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Account Representative"} items={accountRepPie} pieColors={seriesColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Payout Mode"} items={payoutModePie} pieColors={seriesColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Automatic Payments"} items={autoPayPie} pieColors={autoPayColors}/>
                            </Grid>
                            <Grid item lg={3} md={6}>
                                <AccountStatWidget label={"Custom Fees"} items={customFeePie} pieColors={customFeeColors}/>
                            </Grid>

                        </Grid>
                    </Paper>

                    <Paper style={this.styles.paper}>
                        
                        <Typography variant="body2" style={this.styles.paperLabel}>Demographics</Typography>  

                        <Grid container direction="row" spacing={2} style={{padding: 10, marginTop: 10, justifyContent: 'center'}}>

                            <Grid item style={{flexBasis: '50%', maxWidth: '50%'}}>
                                <div style={{textAlign: 'center', marginTop: 20}}>
                                    <Typography variant="body2" style={{fontSize: '14pt', fontWeight: 'bold'}}>Year Acquired</Typography>
                                </div>
                                <Chart
                                    options={this._acquireYearChartOpts}
                                    series={yearAcquiredChartData}
                                    type="bar"
                                    height={window.innerHeight*0.4}
                                />
                            </Grid>
                            <Grid item style={{flexBasis: '25%', maxWidth: '25%'}}>
                                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <div style={{flexGrow: 1, minWidth: 0}}>
                                        <AccountStatWidget label={"US State"} items={salesTaxStatePie}/>
                                    </div>
                                    {otherStateDetail.length > 0 ?
                                        <div style={{border: '1px solid lightgray', borderRadius: 8, minWidth: 30, padding: '4px 8px', fontSize: 11, lineHeight: 1.3, textAlign: 'left', whiteSpace: 'nowrap'}}>
                                            <div style={{fontWeight: 'bold', marginBottom: 2}}>Other:</div>
                                            {otherStateDetail.map(d =>
                                                <div key={d.state} style={{display: 'flex', justifyContent: 'space-between', gap: 8}}>
                                                    <span>{d.state}</span>
                                                    <span>{d.count}</span>
                                                </div>
                                            )}
                                        </div>
                                        : null
                                    }
                                </div>
                                <Typography variant="body2" style={{fontStyle: 'italic', fontSize: '9pt', textAlign: 'center', marginTop: 8}}>Based on State Sales Tax</Typography>
                            </Grid>
                            <Grid item style={{flexBasis: '25%', maxWidth: '25%'}}>
                                <AccountStatWidget label={"Community Type"} items={communityTypePie}/>
                            </Grid>

                        </Grid>
                    </Paper>
                </Collapse>

                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginTop: 20, marginBottom: 15}}>
                    <Autocomplete
                        style={{width: 200}}
                        disableClearable={true}
                        size='small'
                        value={this.state.sortBy}
                        getOptionLabel={(option) => option.label}
                        onChange={(event, newValue) => { this._sortByCallback(newValue, this.state.miniAccountList); }}
                        options={StatisticsPage.sortOptions}
                        blurOnSelect
                        renderOption={(option) => {
                            return (
                            <span>
                                {option.label}
                            </span>
                            );
                        }}
                        renderInput={(params) => <TextField {...params} label="Sort By" variant="outlined" InputLabelProps={{ shrink: true }} 
                                                InputProps={{ ...params.InputProps }}/>}
                    />

                    <Tooltip title="Filter the list by any part of the account number, database, community name, or HOC">
                        <TextField
                            style={{width: 250, marginLeft: 10}}
                            label="Filter"
                            variant="outlined"
                            size="small"
                            value={this.state.filterText}
                            InputLabelProps={{ shrink: true}} 
                            onChange={this._filterTextChanged}
                        />
                    </Tooltip>

                    <Button variant="outlined" size="small" style={{marginLeft: 'auto'}} onClick={this._copyAllEmailAddresses}>Copy All HOC Emails</Button>  
                </div>

                <div ref={this._tableWrapperRef} style={{display: 'flow-root', minHeight: this.state.tableMinHeight}}>
                    <table style={this.styles.table}>
                        <thead>
                            {this._renderRow(null, 0) /*render header*/ }
                        </thead>
                        <tbody>
                            {this.state.miniAccountList.map((miniAccount, index) => this._renderRow(miniAccount, index+1, this.state.filterText))}
                        </tbody>
                    </table>
                </div>

            </Fragment>
        );
        
    }
}



export default withCookies(withRouter(StatisticsPage));
